"use client";

import { Card, CardContent, Typography, Chip, Avatar, Box } from "@mui/material";
import Image from "next/image";
import Link from "next/link";
import { ClipsShareActionsMenu } from "./ClipShareActions/clipsShareActionsMenu";
import { useState } from "react";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PersonIcon from "@mui/icons-material/Person";
import { formatSeconds } from "../helpers";
import { getMeetingUrl } from "../helpers";


interface ClipShareDisplayProps {
  bookmark_id: number;
  creator_email?: string;
  description?: string;
  id: number;
  is_owner: boolean;
  keyword: string;
  meeting_id: number;
  start_time_in_seconds: number;
  tagged_users?: string[];
  thumbnail_url?: string;
  title: string;
  transcript_doc_id: string;
}

export default function ClipShareDisplay({
  id,
  bookmark_id,
  title,
  description,
  thumbnail_url,
  creator_email,
  start_time_in_seconds,
  transcript_doc_id,
  meeting_id,
  keyword,
  tagged_users,
  is_owner,
}: ClipShareDisplayProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const getAvatarColor = (email: string) => {
    const colors = [
      '#566f88', // brand-2 / primary main
      '#0d9dda', // secondary-500 / secondary main
      '#7fa4ca', // brand-3 / primary-500
      '#5a748f', // primary-700
      '#3db1e1', // secondary-400
      '#465a6f', // primary-800
      '#0c8fc6', // secondary-600
      '#99b6d5', // primary-400
      '#096f9b', // secondary-700
      '#7495b8', // primary-600
    ];
    const index = email.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) % colors.length;
    return colors[index];
  };

  return (
    <Card
      className={`mb-2 transition-all duration-300 ease-in-out cursor-pointer bg-white ${isMenuOpen
          ? 'border-2 border-primary shadow-[0_0_0_3px_rgba(86,111,136,0.15)] scale-[1.01]'
          : 'border border-gray-400 shadow-none scale-100'
        } hover:shadow-[0_4px_12px_rgba(0,0,0,0.15)] hover:-translate-y-0.5 hover:border-primary hover:bg-[rgba(127,164,202,0.03)]`}
    >
      <CardContent className="p-0">
        <div className="flex items-center gap-4 p-3">
          <Link
            href={getMeetingUrl(meeting_id, transcript_doc_id, keyword)}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-4 flex-grow min-w-0 no-underline hover:opacity-90 transition-opacity"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex-shrink-0 relative">
              {thumbnail_url ? (
                <Image
                  src={thumbnail_url}
                  alt={title}
                  width={135}
                  height={76}
                  className="rounded object-cover"
                />
              ) : (
                <div className="w-[135px] h-[76px] bg-gray-200 rounded flex items-center justify-center">
                  <Typography variant="caption" color="text.secondary">
                    No thumbnail
                  </Typography>
                </div>
              )}
              <Chip
                icon={<AccessTimeIcon className="text-[14px]" />}
                label={formatSeconds(start_time_in_seconds)}
                size="small"
                className="absolute bottom-1 right-1 h-5 text-[0.7rem] font-semibold bg-black/75 text-white [&_.MuiChip-icon]:text-white [&_.MuiChip-icon]:ml-1"
              />
            </div>

            <div className="flex-grow min-w-0">
              <Typography variant="subtitle1" className="font-semibold truncate">
                {title}
              </Typography>
              {description && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  className="line-clamp-2 mt-1"
                >
                  {description}
                </Typography>
              )}
              {creator_email && (
                <Typography variant="caption" color="text.secondary" className="mt-1 block">
                  Created by {creator_email}
                </Typography>
              )}

              {tagged_users && tagged_users.length > 0 && (
                <Box className="mt-3">
                  <Box className="flex items-center gap-1 mb-1">
                    <PersonIcon className="text-[14px] text-gray-600" />
                    <Typography variant="caption" color="text.secondary" className="font-medium">
                      Tagged Users ({tagged_users.length})
                    </Typography>
                  </Box>
                  <Box className="flex flex-wrap gap-1 max-h-[60px] overflow-y-auto [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-thumb]:bg-black/20 [&::-webkit-scrollbar-thumb]:rounded">
                    {tagged_users.map((email, index) => (
                      <Chip
                        key={index}
                        avatar={
                          <Avatar
                            className="w-5 h-5 !text-white"
                            style={{ backgroundColor: getAvatarColor(email) }}
                          />
                        }
                        label={email}
                        size="small"
                        className="h-6 text-[0.7rem] bg-black/[0.04] hover:bg-black/[0.08]"
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </div>
          </Link>

          {is_owner && <ClipsShareActionsMenu
            clipShareId={id}
            bookmarkId={bookmark_id}
            title={title}
            onOpenChange={setIsMenuOpen}
          />}
        </div>
      </CardContent>
    </Card>
  );
}